import { useEffect, useState } from 'react'
import type {
  SchemaFocusDomain,
  SchemaQuestionTemplate,
  SchemaSessionStartBody,
} from '@/services/api/schemaSessions'
import { emptyIntake } from '../intake'

export function useSchemaIntakeForm(context: { caseId: string; initial?: SchemaSessionStartBody }) {
  const [selected, setSelected] = useState<string[]>([])
  const [investigationGoal, setInvestigationGoal] = useState('')
  const [questions, setQuestions] = useState<SchemaQuestionTemplate[]>([])
  const [focusDomains, setFocusDomains] = useState<SchemaFocusDomain[]>([])
  const [customQuestions, setCustomQuestions] = useState('')

  const storageKey = `schema-intake:${context.caseId}${context.initial ? ':revision' : ''}`
  const [restored, setRestored] = useState(false)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey)
      const saved = context.initial ?? (raw ? (JSON.parse(raw) as SchemaSessionStartBody) : null)
      if (saved) {
        setSelected(saved.documentIds ?? [])
        setInvestigationGoal(saved.investigationGoal ?? '')
        setQuestions(saved.primaryQuestions ?? [])
        setFocusDomains(saved.focusDomains ?? [])
        setCustomQuestions((saved.customQuestions ?? []).join('\n'))
      }
    } catch {
      /* Storage can be unavailable; the form remains usable. */
    }
    setRestored(true)
  }, [storageKey, context.initial])

  const buildIntake = (): SchemaSessionStartBody => {
    const custom = customQuestions
      .split('\n')
      .map(value => value.trim())
      .filter(Boolean)
    return {
      // Preserve metadata on revisions, but don't restore obsolete hidden fields
      // from unfinished legacy drafts (e.g. invalid dates the user can no longer fix).
      ...(context.initial ?? emptyIntake()),
      documentIds: selected,
      investigationGoal: investigationGoal.trim(),
      primaryQuestions: questions,
      // Focus areas stand in for the files; a selection of files makes them moot.
      focusDomains: selected.length === 0 ? focusDomains : [],
      customQuestions: custom,
    }
  }

  const serialized = JSON.stringify(buildIntake())
  useEffect(() => {
    if (!restored) return
    try {
      sessionStorage.setItem(storageKey, serialized)
    } catch {
      /* Keep editing without storage. */
    }
  }, [restored, serialized, storageKey])

  return {
    selected,
    setSelected,
    investigationGoal,
    setInvestigationGoal,
    questions,
    setQuestions,
    focusDomains,
    setFocusDomains,
    customQuestions,
    setCustomQuestions,
    buildIntake,
  }
}
